import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SaveButton from "../../../components/button/SaveButton";
import { MainContainer } from "../../../components/container/MainContainer";
import ExportOrganizationPPTButton from "../../../components/export/ExportOrganizationPPTButton";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Emission_detail } from "../../../db_access/emission_details";
import { GHG_Assessment } from "../../../db_access/ghg_assessment";
import { Supply_chain } from "../../../db_access/supply_chain";
import ScopeFootprint from "../../../organizational-page-components/ScopeFootprint";
import DocumentViewer from "../../../organizational-page-components/DocumentViewer";
import routing from "../../../routing";
import OrganizationInsight from "../Organization_Insight";
import ActionableInsightsView from "../ActionableInsights/ActionableInsightsView";
import OrganizationLifeCycleAssessment from "../OrganizationLifeCycleAssessment";
import OrganizationScope from "../OrganizationScope";
import SankeyDiagram from "../SankeyDiagram";
import { WorkspaceHeader, M3Button, Chip } from "../../../components/m3";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecondaryOpinionSheet from "../../../organizational-page-components/SecondaryOpinionSheet";
import { scope1Categories, scope2Categories, scope3Categories } from "../ScopeCategory";
import "../../../style/pages-style/Organization/organizationDashboard.css";

const SupplyChainWorkspace = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { refresh, selectedSupplyChain, selectedSupplyChainId, setIsLoading, setRefresh } = useContextProvider();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [emissions, setEmissions] = useState([]);
  const [emissionDetails, setEmissionDetails] = useState([]);
  const [ai_insight, setAI_insight] = useState();
  const [ghg, setGHG] = useState(null);
  const [activeTab, setActiveTab] = useState("vault");
  const [isSecondaryOpinionOpen, setIsSecondaryOpinionOpen] = useState(false);

  const validationStatus = selectedSupplyChain?.validation_status || "Not Validated";
  const isLocked = validationStatus === "Pending" || validationStatus === "Validated";

  useEffect(() => {
    if (!selectedSupplyChainId) {
      setEmissions([]);
      setIsLoading(false);
      return;
    }
    Supply_chain.getEmissions(selectedSupplyChainId)
      .then((res) => res.json())
      .then((data) => setEmissions(data))
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedSupplyChainId, refresh, activeTab, setIsLoading]);

  useEffect(() => {
    if (!selectedSupplyChain) {
      navigate(routing.organization.supply_chain.dashboard);
      return;
    }
  }, [selectedSupplyChain, navigate]);

  useEffect(() => {
    if (emissions.length === 0) {
      setEmissionDetails([]);
      setIsLoading(false);
      return;
    }
    Emission_detail.get_by_emission_ids(emissions.map((e) => e.id))
      .then((res) => res.json())
      .then((data) => setEmissionDetails(data))
      .finally(() => {
        setIsLoading(false);
      });
  }, [emissions, setIsLoading]);

  useEffect(() => {
    if (!selectedSupplyChainId) return;
    Supply_chain.get_insight(selectedSupplyChainId)
      .then((res) => res.json())
      .then((data) => setAI_insight(data));
  }, [selectedSupplyChainId, refresh, activeTab]);

  useEffect(() => {
    if (!selectedSupplyChainId) {
      setGHG(null);
      return;
    }
    let ignored = false;
    GHG_Assessment.get_by_supply_chain_id(selectedSupplyChainId)
      .then((res) => res.json())
      .then((data) => {
        if (ignored) return;
        if (data) {
          setGHG({ ...data, is_scope_3_included: data.is_scope_3_included === 1 });
        } else {
          GHG_Assessment.new({ supply_chain_id: selectedSupplyChainId })
            .then((res) => res.json())
            .finally(() => { if (!ignored) setRefresh((r) => !r); });
        }
      });
    return () => { ignored = true; };
  }, [selectedSupplyChainId, refresh, setRefresh]);

  const includesCategory = (categories, cat) => {
    if (!cat) return false;
    const normalized = cat.toLowerCase().trim();
    return categories.some(c => c.toLowerCase().trim() === normalized);
  };
  const assetGWP = useMemo(() => emissions.reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope1 = useMemo(() => emissions.filter(e => includesCategory(scope1Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope2 = useMemo(() => emissions.filter(e => includesCategory(scope2Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope3 = useMemo(() => emissions.filter(e => includesCategory(scope3Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);

  return (
    <MainContainer>
      {/* ── Workspace Page Heading & Action Bar (Combined) ─────────── */}
      <Box sx={{ mb: 3 }}>
        <WorkspaceHeader
          tabs={[
            { value: "vault", label: t("GHG Inventory") },
            { value: "analytics", label: t("Results") },
            { value: "actionable_insights", label: t("Data to Actionable Insight") },
            { value: "evidence", label: t("Evidence Repository") },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {validationStatus === "Pending" && (
                <Chip
                  size="md"
                  tone="warning"
                  icon={<ScheduleOutlinedIcon style={{ fontSize: "16px" }} />}
                  tooltip={t("Inventory is under review. Uploads and editing are locked.")}
                >
                  {t("Pending Validation")}
                </Chip>
              )}
              {validationStatus === "Validated" && (
                <Chip
                  size="md"
                  tone="success"
                  icon={<CheckCircleOutlineIcon style={{ fontSize: "16px" }} />}
                  tooltip={t("Inventory is validated. Uploads and editing are locked.")}
                >
                  {t("Validated")}
                </Chip>
              )}
              {!isLocked && (
                <M3Button
                  variant="outlined"
                  size="md"
                  icon={<RateReviewOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setIsSecondaryOpinionOpen(true)}
                >
                  {t("Request Second Opinion")}
                </M3Button>
              )}
              <ExportOrganizationPPTButton emissions={emissions} ai_insight={ai_insight} emissionDetails={emissionDetails} entityType={Supply_chain.label} entity={selectedSupplyChain} />
            </Box>
          }
        />

        {isLocked && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: validationStatus === "Validated" ? (isDark ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5") : (isDark ? "rgba(245, 158, 11, 0.12)" : "#fffbeb"),
              border: "1px solid",
              borderColor: validationStatus === "Validated" ? "rgba(16, 185, 129, 0.3)" : "rgba(245, 158, 11, 0.3)",
              color: validationStatus === "Validated" ? "#10b981" : "#d97706",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {validationStatus === "Validated"
                ? t("This inventory is Validated. All uploads and editing are locked.")
                : t("This inventory is Pending Validation (Under Review). All uploads and editing are locked.")}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: selectedSupplyChain ? "block" : "none" }}>
        {/* Dynamic Workspace Content View Switcher */}
        <Box sx={{ minHeight: "600px" }}>

          {/* VIEW 1: GHG Inventory */}
          {activeTab === "vault" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", gap: 3 }}>
              <OrganizationLifeCycleAssessment LCA_type={Supply_chain.label} emissions={emissions} emissionDetails={emissionDetails} ghg={ghg} readOnly={isLocked} validationStatus={validationStatus} />
              <OrganizationScope organizationID={selectedSupplyChainId} organizationType={Supply_chain.label} emissions={emissions} emissionDetails={emissionDetails} readOnly={isLocked} validationStatus={validationStatus} />
            </Box>
          )}

          {/* VIEW 2: Evidence Repository */}
          {activeTab === "evidence" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <DocumentViewer pageType="supply_chain" selectedRow={selectedSupplyChain} />
            </Box>
          )}

          {/* VIEW 3: Results */}
          {activeTab === "analytics" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", gap: 3 }}>
              <ScopeFootprint emissions={emissions} emissionDetails={emissionDetails} />
              <SankeyDiagram emissions={emissions} emission_details={emissionDetails} />
              <OrganizationInsight ai_insight={ai_insight} organizationType={Supply_chain.label} />
            </Box>
          )}

          {/* VIEW: Data to Actionable Insight */}
          {activeTab === "actionable_insights" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <ActionableInsightsView
                organizationId={selectedSupplyChainId}
                organizationType={Supply_chain.label}
                emissions={emissions}
                emissionDetails={emissionDetails}
                readOnly={isLocked}
              />
            </Box>
          )}


        </Box>
      </Box>
      <SecondaryOpinionSheet
        open={isSecondaryOpinionOpen}
        onClose={() => setIsSecondaryOpinionOpen(false)}
        organizationName={selectedSupplyChain?.name || "Supply Chain Inventory"}
        organizationId={selectedSupplyChainId}
        organizationType={Supply_chain.label}
        ghg={ghg}
        emissions={emissions}
        assetGWP={assetGWP}
        totalScope1={totalScope1}
        totalScope2={totalScope2}
        totalScope3={totalScope3}
        onValidationSuccess={() => setRefresh((r) => !r)}
      />
      {!isLocked && <SaveButton />}
    </MainContainer>
  );
};

export default SupplyChainWorkspace;
